import { useMemo, useState } from "react"
import { CheckIcon } from "@heroicons/react/24/outline"
import type { Filter, Query } from "@cubejs-client/core"
import Chip from "~/ui-rtk/components/ui/common/Chip"
import DebouncedInput from "~/ui-rtk/components/ui/controls/DebouncedInput/DebouncedInput"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"

/**
 * Form-level "Feed products" multi-select: chips for the creative's selected
 * products + a product search over the `mega_feed` cube view (same search the
 * per-asset ProductTagPopover uses). Pure controlled input — persisting the
 * selection (as MANUAL AssetProductTags on the creative's assets) happens on
 * draft save via `productRefIds` in the creative payload.
 */

const PREFIX = "mega_feed"
const SEARCH_LIMIT = 20

export interface SelectedFeedProduct {
  productRefId: string
  title: string
  imageUrl?: string
}

interface ProductMultiSelectProps {
  value: SelectedFeedProduct[]
  onChange: (next: SelectedFeedProduct[]) => void
  disabled?: boolean
}

export default function ProductMultiSelect({
  value,
  onChange,
  disabled,
}: ProductMultiSelectProps) {
  const [term, setTerm] = useState("")

  // Title contains OR exact id match, so pasting an item_group_id works too.
  const searchQuery = useMemo<Query>(
    () => ({
      dimensions: [
        `${PREFIX}.item_group_id`,
        `${PREFIX}.parent_title`,
        `${PREFIX}.image_link`,
      ],
      filters: [
        {
          or: [
            {
              member: `${PREFIX}.parent_title`,
              operator: "contains",
              values: [term],
            },
            {
              member: `${PREFIX}.item_group_id`,
              operator: "equals",
              values: [term],
            },
          ],
        },
      ] as Filter[],
      limit: SEARCH_LIMIT,
    }),
    [term],
  )
  const {
    resultSet,
    isLoading: isSearching,
    error: searchError,
  } = useCubeQuery(searchQuery, { skip: !term || disabled })

  const results = useMemo<SelectedFeedProduct[]>(() => {
    if (!term || !resultSet) return []
    return (resultSet.rawData() as Record<string, unknown>[])
      .map(row => ({
        productRefId: String(row[`${PREFIX}.item_group_id`] ?? "").trim(),
        title: String(
          row[`${PREFIX}.parent_title`] ?? row[`${PREFIX}.item_group_id`] ?? "",
        ).trim(),
        imageUrl: String(row[`${PREFIX}.image_link`] ?? "").trim() || undefined,
      }))
      .filter(row => row.productRefId)
  }, [term, resultSet])

  const selectedRefIds = useMemo(
    () => new Set(value.map(p => p.productRefId)),
    [value],
  )

  const handleToggle = (product: SelectedFeedProduct) => {
    if (selectedRefIds.has(product.productRefId)) return
    onChange([...value, product])
  }

  const handleRemove = (productRefId: string) => {
    onChange(value.filter(p => p.productRefId !== productRefId))
  }

  return (
    <div className="flex flex-col gap-2">
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {value.map(product => (
            <Chip
              key={product.productRefId}
              className="bg-brand/15 text-brand text-[11px] !px-2 !py-0.5"
              onRemove={
                disabled ? undefined : () => handleRemove(product.productRefId)
              }
            >
              {product.title || product.productRefId}
            </Chip>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-system-gray-500">
          No products selected — the ad's assets won't be added to any feed
          pool.
        </p>
      )}

      <DebouncedInput
        delay={300}
        fullWidth
        placeholder="Search feed products…"
        disabled={disabled}
        className="border-system-gray-700/50 bg-system-gray-900/50"
        classes={{ input: "text-white placeholder-system-gray-600" }}
        onChange={e => setTerm(e.target.value.trim())}
      />

      {term &&
        !disabled &&
        (searchError ? (
          <p className="text-[11px] text-error">
            Product search is unavailable right now — the feed snapshot couldn't
            be queried.
          </p>
        ) : isSearching ? (
          <div className="flex items-center justify-center py-3">
            <span className="loading loading-spinner loading-xs text-brand" />
          </div>
        ) : !resultSet ? (
          <p className="text-[11px] text-system-gray-500">
            Product search is unavailable — no feed snapshot for this company.
          </p>
        ) : results.length === 0 ? (
          <p className="text-[11px] text-system-gray-500">
            No feed products found
          </p>
        ) : (
          <div className="max-h-56 overflow-y-auto flex flex-col gap-0.5 -mx-1">
            {results.map(row => {
              const alreadySelected = selectedRefIds.has(row.productRefId)
              return (
                <button
                  key={row.productRefId}
                  type="button"
                  disabled={alreadySelected}
                  onClick={() => handleToggle(row)}
                  className="flex items-center gap-2 px-1 py-1 rounded-md text-left hover:bg-system-gray-800 transition disabled:opacity-60 disabled:hover:bg-transparent"
                >
                  {row.imageUrl ? (
                    <img
                      src={row.imageUrl}
                      alt=""
                      loading="lazy"
                      className="w-8 h-8 rounded object-cover bg-system-gray-800 shrink-0"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded bg-system-gray-800 shrink-0" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs text-white truncate">
                      {row.title || row.productRefId}
                    </span>
                    <span className="block text-[10px] text-system-gray-500 truncate">
                      {row.productRefId}
                    </span>
                  </span>
                  {alreadySelected && (
                    <CheckIcon className="w-3.5 h-3.5 text-brand shrink-0" />
                  )}
                </button>
              )
            })}
          </div>
        ))}
    </div>
  )
}
